import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Widget } from "@typeform/embed-react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import PrimaryNavigation from "components/molecules/PrimaryNavigation";
import Actions from "store/plan/actions";
import authActions from "store/auth/actions";
import profileActions from "store/profile/actions";
import {
  typeform,
  getProfile,
  groupNewAccount,
  groupNewReport,
  getGroupReportInfo,
  addEmail,
  logLogin,
  updateUsername,
} from "services/api";
import { getGroup } from "services/api/groups.api";
import { getUserId, getToken, setToken, setReportToken } from "services/utils";

export const handleFinishTypeformFlow = async ({
  reportId,
  newReport,
  group,
  groupObj,
  email,
  workEmail,
  jobOfferEmail,
  typeformData,
  canRedirectToReport,
  history,
  dispatch,
}) => {
  const token = getToken();
  const { editable, requiresUserPremiums } = groupObj;

  let nextAction;
  if (editable) {
    nextAction = "editPlan";
  } else if (requiresUserPremiums) {
    nextAction = "addPremiums";
  } else if (newReport) {
    nextAction = "newReport";
  } else {
    nextAction = "newAccount";
  }

  switch (nextAction) {
    case "editPlan": {
      const { groupReportId, displayName, premiumTier, waiverTier } = (
        await getGroupReportInfo({ group, additionalInformation: { workEmail, jobOfferEmail } })
      ).data.response;

      dispatch(Actions.Creators.changeCurrent(0));
      history.push("/edit-plan", {
        reportId,
        option: "birthParent",
        reportToVerify: groupReportId,
        workEmail,
        jobOfferEmail,
        group,
        premiumTier,
        waiverTier,
        displayName,
        typeformData,
        canRedirectToReport,
      });
      break;
    }

    case "addPremiums": {
      const { groupReportId, premiumTier } = (
        await getGroupReportInfo({ group, additionalInformation: { workEmail, jobOfferEmail } })
      ).data.response;

      history.push("/add-premiums", {
        reportId,
        option: "birthParent",
        reportToVerify: groupReportId,
        workEmail,
        jobOfferEmail,
        group,
        premiumTier,
        typeformData,
        canRedirectToReport,
      });
      break;
    }

    case "newReport": {
      const [personalRes, workRes] = await Promise.all([
        getProfile(null, token, email.toLowerCase()),
        getProfile(null, token, workEmail.toLowerCase()),
      ]);
      let userId = await getUserId();
      if (!userId && personalRes.status === 200) {
        userId = personalRes.data.user._id;
      }
      if (!userId && workRes.status === 200) {
        userId = workRes.data.user._id;
      }
      const { status: reportStatus, data: reportData } = await groupNewReport({
        userId,
        typeformData: typeformData,
        group,
        additionalInformation: { workEmail, jobOfferEmail },
      });
      if (reportStatus === 200 && canRedirectToReport) {
        deleteLocalStorage();
        if (!(await getUserId())) {
          setReportToken(reportData.reportJWT);
        }
        if (canRedirectToReport) {
          history.push(`/optimizeV2/${reportData.reportId}`, { activeTab: 2 });
        }
      }
      break;
    }

    case "newAccount": {
      const { status: accountStatus, data: accountData } = await groupNewAccount({
        typeformData: typeformData,
        group,
        additionalInformation: { workEmail, jobOfferEmail },
      });
      if (accountStatus === 200 && accountData.authenticatedUser && canRedirectToReport) {
        deleteLocalStorage();
        if (canRedirectToReport) {
          const { username, token } = accountData.authenticatedUser;
          await logLogin({ username: username });
          setToken(token);
          dispatch(authActions.Creators.loginSuccess(username, token));
          dispatch(profileActions.Creators.loadProfileSuccess(accountData.authenticatedUser));
          history.push(`/optimizeV2/${accountData.reportId}`, { activeTab: 2 });
        }
      }
      break;
    }

    default:
      break;
  }
};

const Wrapper = styled(Widget)`
  position: relative;
  height: 100vh;
  overflow-y: hidden;
`;

const deleteLocalStorage = () => {
  localStorage.removeItem("typeformData");
  localStorage.removeItem("form");
};

const NewRecommendationTypeformPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [groupObj, setGroupObj] = useState(null);
  const url = state?.url || "https://predictabill.typeform.com/to/dRDh8Ews?typeform-welcome=0";
  const primary = state?.primary || "";
  const group = state?.group || "";
  const newReport = state?.newReport || false;
  const username = state?.username || null;
  const reportId = state?.reportId;
  const formType = "dRDh8Ews";
  const workEmail = localStorage.getItem("workEmail") || "";
  const jobOfferEmail = localStorage.getItem("jobOfferEmail") || "";

  useEffect(() => {
    const fetchGroup = async () => {
      if (!state?.group) return;
      const res = await getGroup({ name: group, filterFields: true });
      if (res.status === 200) {
        setGroupObj(res.data.group);
      }
    };
    fetchGroup();
  }, [state.group]);

  const handleEmailRouting = async ({
    email,
    workEmail,
    reportId,
    typeformData,
    groupObj,
    canRedirectToReport,
    personalRes,
    workRes,
  }) => {
    const { requiresEmailVerification } = groupObj;
    const personalUser = personalRes.data?.user;
    const workUser = workRes.data?.user;
    const emailDomain = email.split("@")[1];
    const workEmailDomain = workEmail.split("@")[1];
    const { otherDomains } = groupObj;

    // 1) Both exist & same account
    if (personalRes.status === 200 && workRes.status === 200 && personalUser?.username === workUser?.username) {
      return false;
    }

    // 2) Both exist & different account
    if (personalRes.status === 200 && workRes.status === 200 && personalUser?.username !== workUser?.username) {
      history.push("/groups/complete-onboarding", {
        emailFromTypeform: email,
        workEmail,
        group,
        reportId,
        typeformData,
        groupObj,
        type: "two-accounts-issue",
      });
      return true;
    }

    // 3) Only personal exists
    if (personalRes.status === 200) {
      await addEmail(personalUser._id, workEmail, "workEmail", personalUser.token);
      return false;
    }

    // 4) Only work exists
    if (workRes.status === 200) {
      if (!workUser.alternativeEmails || workUser.alternativeEmails?.length === 0 || requiresEmailVerification) {
        await updateUsername(workUser._id, email, "workEmail", workUser.token);
        return false;
      } else {
        history.push("/groups/complete-onboarding", {
          emailFromTypeform: email,
          workEmail,
          group,
          reportId,
          typeformData,
          groupObj,
          type: "re-verify-work-email",
        });
        return true;
      }
    }

    if (email === workEmail || emailDomain === workEmailDomain || otherDomains?.includes(emailDomain)) {
      history.push("/groups/complete-onboarding", {
        emailFromTypeform: email,
        workEmail,
        group,
        reportId,
        typeformData,
        groupObj,
        type: "attach-personal-email",
      });
      return true;
    }
  };

  return (
    <>
      <PrimaryNavigation isTypeform />
      <Wrapper
        id={url}
        hidden={{
          email: username,
        }}
        onSubmit={async (e) => {
          if (!groupObj) return;
          const { data: typeformData } = await typeform({ formType, responseId: e.responseId });
          const email = typeformData?.email?.replace("qu.edu", "quinnipiac.edu") || "";
          typeformData.primary = primary;
          const { specificDrugs, otherServices } = typeformData;

          const canRedirectToReport = !specificDrugs?.length && !otherServices?.length;
          const token = getToken();
          const [personalRes, workRes] = await Promise.all([
            getProfile(null, token, email.toLowerCase()),
            getProfile(null, token, workEmail.toLowerCase()),
          ]);

          if (email) {
            const routingToCompleteOnboarding = await handleEmailRouting({
              email,
              workEmail,
              reportId,
              typeformData,
              groupObj,
              canRedirectToReport,
              personalRes,
              workRes,
            });
            if (routingToCompleteOnboarding) return;
          }

          await handleFinishTypeformFlow({
            reportId,
            newReport: Boolean(newReport || personalRes.status === 200 || workRes.status === 200),
            group,
            groupObj,
            email,
            workEmail,
            jobOfferEmail,
            typeformData,
            canRedirectToReport,
            history,
            dispatch,
          });
        }}
      />
    </>
  );
};

export default NewRecommendationTypeformPage;
